import { z } from 'zod';

export const discrepancyTypeSchema = z.enum([
  'missing_items',
  'damaged_items',
  'wrong_items',
  'quantity_mismatch',
  'quality_issue',
]);

export type DiscrepancyType = z.infer<typeof discrepancyTypeSchema>;

export const discrepancyStatusSchema = z.enum([
  'open',
  'pending_supplier_response',
  'pending_receiver_confirmation',
  'resolved',
  'disputed',
]);

export type DiscrepancyStatus = z.infer<typeof discrepancyStatusSchema>;

export const resolutionTypeSchema = z.enum([
  'credit_note',
  'redelivery',
  'replacement',
  'refund',
  'no_action',
]);

export type ResolutionType = z.infer<typeof resolutionTypeSchema>;

export const discrepancyItemSchema = z.object({
  lineId: z.string(),
  itemName: z.string(),
  sku: z.string(),
  orderedQuantity: z.number(),
  receivedQuantity: z.number(),
  affectedQuantity: z.number(),
  unit: z.string(),
});

export const discrepancySchema = z.object({
  discrepancyId: z.string(),
  poId: z.string(),
  poNumber: z.string(),
  deliveryId: z.string().optional(),
  supplierId: z.string(),
  supplierName: z.string(),
  type: discrepancyTypeSchema,
  status: discrepancyStatusSchema,
  items: z.array(discrepancyItemSchema),
  description: z.string(),
  reportedBy: z.string(),
  reportedByName: z.string(),
  reportedAt: z.string(),
  proposedResolution: resolutionTypeSchema.optional(),
  finalResolution: resolutionTypeSchema.optional(),
  resolvedAt: z.string().optional(),
  resolvedBy: z.string().optional(),
  creditNoteAmount: z.number().optional(),
  currency: z.string(),
});

export type Discrepancy = z.infer<typeof discrepancySchema>;
export type DiscrepancyItem = z.infer<typeof discrepancyItemSchema>;

export const discrepanciesResponseSchema = z.object({data: z.array(discrepancySchema),});

export type DiscrepanciesResponse = z.infer<typeof discrepanciesResponseSchema>;

// Messages
export const messageAttachmentSchema = z.object({
  attachmentId: z.string(),
  fileName: z.string(),
  fileUrl: z.string(),
  fileType: z.string(),
  thumbnailUrl: z.string().optional(),
});

export type MessageAttachment = z.infer<typeof messageAttachmentSchema>;

export const messageSchema = z.object({
  messageId: z.string(),
  discrepancyId: z.string(),
  senderId: z.string(),
  senderName: z.string(),
  senderRole: z.enum(['supplier', 'receiver']),
  content: z.string(),
  attachments: z.array(messageAttachmentSchema).optional(),
  createdAt: z.string(),
});

export type Message = z.infer<typeof messageSchema>;

export const messagesResponseSchema = z.object({data: z.array(messageSchema),});

export type MessagesResponse = z.infer<typeof messagesResponseSchema>;

// Labels
export const DISCREPANCY_TYPE_LABELS: Record<DiscrepancyType, string> = {
  missing_items: 'Missing Items',
  damaged_items: 'Damaged Items',
  wrong_items: 'Wrong Items',
  quantity_mismatch: 'Quantity Mismatch',
  quality_issue: 'Quality Issue',
};

export const DISCREPANCY_STATUS_LABELS: Record<DiscrepancyStatus, string> = {
  open: 'Open',
  pending_supplier_response: 'Awaiting Supplier',
  pending_receiver_confirmation: 'Awaiting Your Confirmation',
  resolved: 'Resolved',
  disputed: 'Disputed',
};

export const RESOLUTION_TYPE_LABELS: Record<ResolutionType, string> = {
  credit_note: 'Credit Note',
  redelivery: 'Redelivery',
  replacement: 'Replacement',
  refund: 'Full Refund',
  no_action: 'No Action Required',
};
